import dotsCircleHorizontalIcon from "@tabler/icons/outline/dots-circle-horizontal.svg";
import dotsIcon from "@tabler/icons/outline/dots.svg";
import React, { useState } from "react";
import { FormattedMessage } from "react-intl";
import { Link } from "react-router-dom";

import Account from "@/components/account";
import Search from "@/components/search/search";
import { HStack, Icon, Popover, Stack } from "@/components/ui";
import { useAuth } from "@/contexts/auth/useAuth";
import ComposeButton from "@/features/ui/components/compose-button";
import ProfileDropdown from "@/features/ui/components/profile-dropdown";

import HomeLogo from "../home-logo";
import GemBadge from "../premium/gem-badge";

import SidebarNavigationLink from "./sidebar-navigation-link";
import { type NavigationKeys, useNavigationItems } from "./useNavigationItems";
import { useOverflowedItems, LINK_HEIGHT } from "./useOverflowedItems";

const POPOVER_PADDING = 8;
/** Desktop sidebar with links to different views in the app. */
const SidebarNavigation = () => {
  const { currentAccount } = useAuth();
  const [isMenuOpened, setIsMenuOpened] = useState(false);

  const navigationItems = useNavigationItems();
  const { mainIds, overflowIds, topRef, navRef, bottomRef, containerRef } =
    useOverflowedItems(navigationItems);

  function renderNavigationItem(key: NavigationKeys, isOverflowItem?: boolean) {
    if (!navigationItems) return;

    const item = navigationItems[key];
    if ("enabled" in item && item.enabled === false) return null;

    if ("customComponent" in item)
      return <React.Fragment key={key}>{item.customComponent}</React.Fragment>;

    return (
      <SidebarNavigationLink
        key={key}
        {...item}
        onClick={() => {
          if (isOverflowItem) setIsMenuOpened(false);
        }}
        className={isOverflowItem ? "rounded-md" : ""}
      />
    );
  }

  return (
    <Stack
      className="h-full justify-between gap-12 pt-2 pb-6"
      ref={containerRef}
    >
      <Stack space={6} ref={topRef}>
        <Link key="logo" to="/" data-preview-title-id="column.home">
          <HomeLogo alt="Logo" className="h-5 w-auto cursor-pointer" />
          <span className="hidden">
            <FormattedMessage id="tabs_bar.home" defaultMessage="Home" />
          </span>
        </Link>

        <Stack space={3}>
          {currentAccount && <Search showSearchHistory />}

          <Stack space={0} ref={navRef}>
            {mainIds.map((key) => renderNavigationItem(key))}

            {overflowIds.length > 0 && (
              <Popover
                interaction="click"
                isFlush
                offsetOptions={-(LINK_HEIGHT + POPOVER_PADDING)}
                isOpen={isMenuOpened}
                onOpenChange={setIsMenuOpened}
                sizeOptions={{
                  apply({ elements, rects }) {
                    elements.floating.style.setProperty(
                      "width",
                      `${rects.reference.width + POPOVER_PADDING * 2}px`,
                    );
                  },
                }}
                containerClassName="sticky p-2 shadow-[0_0_20px_0_rgba(0,0,0,0.1)]! dark:shadow-none"
                content={
                  <>
                    {overflowIds.map((key) => renderNavigationItem(key, true))}
                  </>
                }
              >
                <SidebarNavigationLink
                  onClick={() => {}}
                  icon={dotsCircleHorizontalIcon}
                  activeIcon={dotsCircleHorizontalIcon}
                  text={
                    <FormattedMessage
                      id="tabs_bar.more"
                      defaultMessage="More"
                    />
                  }
                />
              </Popover>
            )}
          </Stack>

          {currentAccount && <ComposeButton />}
        </Stack>
      </Stack>

      {currentAccount && (
        <HStack
          className="w-full gap-4"
          justifyContent="between"
          ref={bottomRef}
        >
          <Account
            account={currentAccount}
            showProfileHoverCard={false}
            hideActions
          />

          <HStack alignItems="center" space={2} className="ml-auto">
            <GemBadge />

            <ProfileDropdown account={currentAccount}>
              <Icon
                src={dotsIcon}
                className="size-5 text-gray-900 dark:text-white"
              />
            </ProfileDropdown>
          </HStack>
        </HStack>
      )}
    </Stack>
  );
};

export default SidebarNavigation;
